import React, { forwardRef, useState } from 'react';

import { useCurrentUserUpdate } from '../../../hooks';
import {
  community_comments_path,
  new_session_path,
  subscribe_promotion_path,
} from '../../../routes';
import request from '../../../utils/backend-api/request';
import { Benefits } from '../../shared';
import StepContainer from './StepContainer';

const FORUM_COMMENT_MODAL_CONTENT = {
  heading: 'Enable your profile to post to the forum',
  subheading: 'Join this discussion',
  linkText: 'Enable Profile & Post to Forum',
};
const ARTICLE_COMMENT_MODAL_CONTENT = {
  heading: 'Enable your profile to comment on this article',
  subheading: 'Join this discussion',
  linkText: 'Enable Profile & Post Comment',
};
const SUBSCRIBER_MODAL_CONTENT = {
  heading: 'Enable your profile to connect with other subscribers',
  subheading: 'Join the community',
  linkText: 'Turn On My Profile',
};
const LEAD_MODAL_CONTENT = {
  heading: 'Subscribe to The Information',
  subheading: "Become a subscriber to view The Information's exclusive subscriber directory",
  linkText: 'Subscribe & Save 25%',
  link: subscribe_promotion_path({ offer: 'pmod-072022' }),
};
const BENEFITS = [
  'Comment on articles',
  'Participate in the subscriber forum',
  'Use the subscriber directory',
  'Message with subscribers',
];

const StepEnableProfile = forwardRef(
  (
    {
      nextStep,
      isLoggedIn,
      isSubscriber,
      isSaving,
      setIsSaving,
      setProfileEnabled,
      isTriggeredByComment,
      isCommentForForum,
      handleSubmitComment,
      commentData,
    },
    triggerCommentRef
  ) => {
    const { updateCurrentUserAsync } = useCurrentUserUpdate();
    const [error, setError] = useState();

    const handleSubmit = async () => {
      setIsSaving(true);
      try {
        await updateCurrentUserAsync({
          profile_enabled: true,
          message_enabled: true,
          // TODO: should this set commenting_enabled as well, to match user.permitted_to_comment?
        });

        // If we are in a webview on the mobile app, send a message indicating that the mobile app
        // should reload the user data.
        window.ReactNativeWebView?.postMessage(JSON.stringify({ event: 'reloadUserData' }));

        setProfileEnabled(true);
        setIsSaving(false);
        // create comment if enabled profile is triggered by comment
        if (isTriggeredByComment) {
          // used for forum posts in react because we can pass submit method (nice)
          if (handleSubmitComment) {
            handleSubmitComment();
          } else {
            // TODO: remove
            // have to pass values and call method to update comments when rendered from rails
            if (triggerCommentRef?.current) {
              // TODO: Remove when we convert article comments to react
              const body = triggerCommentRef?.current.form.querySelector('.comment-editor').value;
              await request('post', community_comments_path(), {
                community_comment: { ...commentData, body },
              });
            }
          }
        }
        nextStep();
      } catch (err) {
        setError(err);
        setIsSaving(false);
      }
    };

    let modalContent = {};
    if (isSubscriber) {
      if (isTriggeredByComment) {
        modalContent = isCommentForForum
          ? FORUM_COMMENT_MODAL_CONTENT
          : ARTICLE_COMMENT_MODAL_CONTENT;
      } else {
        modalContent = SUBSCRIBER_MODAL_CONTENT;
      }
    } else {
      modalContent = LEAD_MODAL_CONTENT;
    }

    return (
      <StepContainer
        heading={modalContent.heading}
        subheading={modalContent.subheading}
        error={error}
      >
        <div>
          <div className="action-container">
            {isSubscriber ? (
              <button disabled={isSaving} onClick={handleSubmit}>
                {modalContent.linkText}
              </button>
            ) : (
              <a href={modalContent.link}>{modalContent.linkText}</a>
            )}
          </div>
          {!isLoggedIn && (
            <div className="log-in-wrapper">
              Already have an account? <a href={new_session_path()}>Log in here</a>
            </div>
          )}
          <Benefits benefits={BENEFITS} topMargin="Large" />
        </div>
      </StepContainer>
    );
  }
);
StepEnableProfile.displayName = 'StepEnableProfile';

export default StepEnableProfile;
